import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import {
  getSaActivityLogs,
  SaActivityLog,
} from "../../../helpers/super_admin_ops_helper";

const PER_PAGE = 25;

/** Códigos gravados pelo backend — filtro e i18n alinhados (LAC-042/065). */
const ACTION_CODES = [
  "tenant.blocked",
  "tenant.unblocked",
  "tenant.impersonate",
  "tenant.ramp_updated",
  "tenant.profile_updated",
  "tenant.created",
  "impersonation.write",
  "email_circuit.opened",
  "email_circuit.reset",
  "email_circuit.auto_closed",
  "email_circuit.second_pause_notified",
  "email_circuit.send_resumed",
  "platform_freeze.opened",
  "platform_freeze.cleared",
  "platform_freeze.auto_cleared",
  "email_suppression.created",
  "email_suppression.deleted",
  "domain_suppression.created",
  "domain_suppression.deleted",
  "plan.gateway_refs_cleared",
  "plan.stale_gateway_refs_detected",
] as const;

type ActionSeverity = "danger" | "warning" | "success" | "info" | "secondary";

/**
 * Trilha de auditoria SA — GET /super-admin/activity-logs (LAC-065 · DOC 02 § 9).
 * JSON old/new só no modal; filtros usam query já suportada pela API.
 */
const SuperAdminActivityLogs = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || "pt-BR";
  const [searchParams] = useSearchParams();

  const initialAction = searchParams.get("action") || "";
  const initialTenantRaw = searchParams.get("tenant_id") || "";
  const initialTenant =
    initialTenantRaw && /^\d+$/.test(initialTenantRaw)
      ? Number(initialTenantRaw)
      : undefined;

  const [items, setItems] = useState<SaActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [detail, setDetail] = useState<SaActivityLog | null>(null);
  const [copyBusy, setCopyBusy] = useState(false);

  // Filtros “rascunho” vs aplicados — Apply evita refetch a cada tecla no tenant_id.
  const [actionDraft, setActionDraft] = useState(initialAction);
  const [tenantDraft, setTenantDraft] = useState(
    initialTenant != null ? String(initialTenant) : ""
  );
  const [actionFilter, setActionFilter] = useState(initialAction);
  const [tenantFilter, setTenantFilter] = useState<number | undefined>(
    initialTenant
  );

  const filtersActive = Boolean(actionFilter) || tenantFilter != null;

  document.title = t("sa.activityLogs.documentTitle");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getSaActivityLogs({
        page,
        per_page: PER_PAGE,
        action: actionFilter || undefined,
        tenant_id: tenantFilter,
      });
      if (!res?.success) {
        setError(t("sa.activityLogs.error.load"));
        setItems([]);
        return;
      }
      setItems(res.data || []);
      setTotal(res.meta?.total ?? 0);
      setLastPage(res.meta?.last_page ?? 1);
    } catch (e: unknown) {
      const msg =
        e && typeof e === "object" && "message" in e
          ? String((e as { message?: string }).message)
          : null;
      setError(msg || t("sa.activityLogs.error.load"));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [page, actionFilter, tenantFilter, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const applyFilters = (e: React.FormEvent) => {
    e.preventDefault();
    const raw = tenantDraft.trim();
    let nextTenant: number | undefined;
    if (raw !== "") {
      const n = Number.parseInt(raw, 10);
      if (!Number.isFinite(n) || n < 1) {
        toast.error(t("sa.activityLogs.filter.tenantInvalid"), { autoClose: 3000 });
        return;
      }
      nextTenant = n;
    }
    setActionFilter(actionDraft);
    setTenantFilter(nextTenant);
    setPage(1);
  };

  const clearFilters = () => {
    setActionDraft("");
    setTenantDraft("");
    setActionFilter("");
    setTenantFilter(undefined);
    setPage(1);
  };

  const actorKindLabel = (row: SaActivityLog): string => {
    const type = row.actor_type || "";
    if (type.includes("SuperAdmin")) {
      return t("sa.activityLogs.actor.superAdmin");
    }
    if (type.includes("User")) {
      return t("sa.activityLogs.actor.user");
    }
    return t("sa.activityLogs.actor.system");
  };

  const actorDisplay = (row: SaActivityLog): string => {
    const kind = actorKindLabel(row);
    if (row.actor_label) {
      return `${kind} (${row.actor_label})`;
    }
    return kind;
  };

  const actionLabel = (action: string): string =>
    t(`sa.activityLogs.actions.${action}`, { defaultValue: action });

  /** Severidade visual — crítico / suporte / ok / neutro. */
  const actionSeverity = (action: string): ActionSeverity => {
    if (
      action === "tenant.blocked" ||
      action === "platform_freeze.opened" ||
      action === "email_circuit.opened"
    ) {
      return "danger";
    }
    if (
      action === "impersonation.write" ||
      action === "tenant.impersonate" ||
      action.endsWith(".created")
    ) {
      return "warning";
    }
    if (
      action === "tenant.unblocked" ||
      action === "email_circuit.reset" ||
      action === "email_circuit.auto_closed" ||
      action === "email_circuit.send_resumed" ||
      action === "platform_freeze.cleared" ||
      action === "platform_freeze.auto_cleared" ||
      action.endsWith(".deleted")
    ) {
      return "success";
    }
    return "secondary";
  };

  const formatWhen = (iso: string | null): string => {
    if (!iso) {
      return "—";
    }
    return new Date(iso).toLocaleString(locale);
  };

  const hasDetails = (row: SaActivityLog): boolean =>
    Boolean(row.old_values) ||
    Boolean(row.new_values) ||
    Boolean(row.route) ||
    Boolean(row.impersonator_id);

  const prettyJson = (value: Record<string, unknown> | null): string => {
    if (!value) {
      return "—";
    }
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  };

  const detailPayload = useMemo(() => {
    if (!detail) {
      return "";
    }
    return JSON.stringify(
      {
        id: detail.id,
        action: detail.action,
        old_values: detail.old_values,
        new_values: detail.new_values,
        route: detail.route,
        ip: detail.ip,
        tenant_id: detail.tenant_id,
        impersonator_id: detail.impersonator_id,
        created_at: detail.created_at,
      },
      null,
      2
    );
  }, [detail]);

  const copyDetails = async () => {
    if (!detailPayload) {
      return;
    }
    setCopyBusy(true);
    try {
      await navigator.clipboard.writeText(detailPayload);
      toast.success(t("sa.activityLogs.copySuccess"), { autoClose: 2000 });
    } catch {
      toast.error(t("sa.activityLogs.copyError"), { autoClose: 3000 });
    } finally {
      setCopyBusy(false);
    }
  };

  const renderTenantCell = (row: SaActivityLog) => {
    if (row.tenant_id == null) {
      return "—";
    }
    const label = row.tenant_name || `#${row.tenant_id}`;
    return (
      <Link to={`/super-admin/tenants/${row.tenant_id}`} className="fw-medium">
        {label}
      </Link>
    );
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("sa.activityLogs.title")}
          pageTitle={t("sa.menu.ops")}
        />
        <ToastContainer closeButton={false} limit={2} />

        {error && (
          <Alert color="danger" className="d-flex justify-content-between gap-2">
            <span>{error}</span>
            <Button color="light" size="sm" onClick={() => void load()}>
              {t("sa.activityLogs.retry")}
            </Button>
          </Alert>
        )}

        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader>
                <h5 className="card-title mb-0">{t("sa.activityLogs.title")}</h5>
                <p className="text-muted small mb-0 mt-1">
                  {t("sa.activityLogs.subtitle")}
                </p>
              </CardHeader>
              <CardBody>
                <FormFilters
                  actionDraft={actionDraft}
                  tenantDraft={tenantDraft}
                  onActionChange={setActionDraft}
                  onTenantChange={setTenantDraft}
                  onSubmit={applyFilters}
                  onClear={clearFilters}
                  filtersActive={filtersActive}
                  actionCodes={ACTION_CODES}
                  actionLabel={actionLabel}
                  t={t}
                />

                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                  </div>
                ) : items.length === 0 ? (
                  <div className="text-center py-5">
                    <p className="text-muted mb-0">
                      {filtersActive
                        ? t("sa.activityLogs.emptyFiltered")
                        : t("sa.activityLogs.empty")}
                    </p>
                    {filtersActive ? (
                      <Button
                        color="light"
                        size="sm"
                        className="mt-3"
                        onClick={clearFilters}
                      >
                        {t("sa.activityLogs.filter.clear")}
                      </Button>
                    ) : null}
                  </div>
                ) : (
                  <>
                    <p className="text-muted small mb-2">
                      {t("sa.activityLogs.totalCount", { count: total })}
                    </p>
                    <div className="table-responsive">
                      <Table className="align-middle table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>{t("sa.activityLogs.columns.when")}</th>
                            <th>{t("sa.activityLogs.columns.actor")}</th>
                            <th>{t("sa.activityLogs.columns.action")}</th>
                            <th>{t("sa.activityLogs.columns.tenant")}</th>
                            <th>{t("sa.activityLogs.columns.ip")}</th>
                            <th className="text-end">
                              {t("sa.activityLogs.columns.details")}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((row) => {
                            const underImpersonation = Boolean(
                              row.impersonator_id ||
                                row.action === "impersonation.write"
                            );
                            return (
                              <tr
                                key={row.id}
                                className={
                                  underImpersonation ? "table-warning" : undefined
                                }
                              >
                                <td className="small text-muted">
                                  {formatWhen(row.created_at)}
                                </td>
                                <td>
                                  <span>{actorDisplay(row)}</span>
                                  {underImpersonation ? (
                                    <Badge
                                      color="warning"
                                      className="ms-1 align-middle"
                                    >
                                      {t("sa.activityLogs.impersonationBadge")}
                                    </Badge>
                                  ) : null}
                                </td>
                                <td>
                                  <Badge
                                    color={actionSeverity(row.action)}
                                    className="text-wrap text-start"
                                  >
                                    {actionLabel(row.action)}
                                  </Badge>
                                </td>
                                <td>{renderTenantCell(row)}</td>
                                <td className="small font-monospace">
                                  {row.ip || "—"}
                                </td>
                                <td className="text-end">
                                  {hasDetails(row) ? (
                                    <Button
                                      color="soft-secondary"
                                      size="sm"
                                      onClick={() => setDetail(row)}
                                    >
                                      {t("sa.activityLogs.viewDetails")}
                                    </Button>
                                  ) : (
                                    "—"
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                    {lastPage > 1 && (
                      <div className="d-flex justify-content-between mt-3">
                        <Button
                          color="light"
                          size="sm"
                          disabled={page <= 1}
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                        >
                          {t("sa.activityLogs.prev")}
                        </Button>
                        <span className="text-muted small align-self-center">
                          {t("sa.activityLogs.pageOf", {
                            page,
                            lastPage,
                          })}
                        </span>
                        <Button
                          color="light"
                          size="sm"
                          disabled={page >= lastPage}
                          onClick={() =>
                            setPage((p) => Math.min(lastPage, p + 1))
                          }
                        >
                          {t("sa.activityLogs.next")}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal
        isOpen={!!detail}
        toggle={() => setDetail(null)}
        centered
        size="lg"
      >
        <ModalHeader toggle={() => setDetail(null)}>
          {t("sa.activityLogs.detailsTitle")}
        </ModalHeader>
        <ModalBody>
          {detail ? (
            <>
              {Boolean(detail.impersonator_id) ||
              detail.action === "impersonation.write" ? (
                <Alert color="warning" className="py-2 small">
                  {t("sa.activityLogs.impersonationHint")}
                </Alert>
              ) : null}
              <dl className="row mb-3 small">
                <dt className="col-sm-3">{t("sa.activityLogs.columns.action")}</dt>
                <dd className="col-sm-9">
                  <Badge color={actionSeverity(detail.action)}>
                    {actionLabel(detail.action)}
                  </Badge>
                </dd>
                <dt className="col-sm-3">{t("sa.activityLogs.columns.when")}</dt>
                <dd className="col-sm-9">{formatWhen(detail.created_at)}</dd>
                <dt className="col-sm-3">{t("sa.activityLogs.columns.actor")}</dt>
                <dd className="col-sm-9">{actorDisplay(detail)}</dd>
                <dt className="col-sm-3">{t("sa.activityLogs.columns.tenant")}</dt>
                <dd className="col-sm-9">{renderTenantCell(detail)}</dd>
                <dt className="col-sm-3">{t("sa.activityLogs.columns.ip")}</dt>
                <dd className="col-sm-9 font-monospace">{detail.ip || "—"}</dd>
                {detail.route ? (
                  <>
                    <dt className="col-sm-3">
                      {t("sa.activityLogs.columns.route")}
                    </dt>
                    <dd className="col-sm-9 font-monospace small">
                      {detail.route}
                    </dd>
                  </>
                ) : null}
              </dl>
              <p className="fw-medium mb-1">{t("sa.activityLogs.oldValues")}</p>
              <pre className="bg-light border rounded p-2 small mb-3 overflow-auto">
                {prettyJson(detail.old_values)}
              </pre>
              <p className="fw-medium mb-1">{t("sa.activityLogs.newValues")}</p>
              <pre className="bg-light border rounded p-2 small mb-0 overflow-auto">
                {prettyJson(detail.new_values)}
              </pre>
            </>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button
            color="soft-primary"
            disabled={copyBusy || !detailPayload}
            onClick={() => void copyDetails()}
          >
            {copyBusy ? <Spinner size="sm" /> : t("sa.activityLogs.copyJson")}
          </Button>
          <Button color="light" onClick={() => setDetail(null)}>
            {t("sa.activityLogs.close")}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

/** Barra de filtros — isolada para manter o componente principal legível. */
function FormFilters(props: {
  actionDraft: string;
  tenantDraft: string;
  onActionChange: (v: string) => void;
  onTenantChange: (v: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onClear: () => void;
  filtersActive: boolean;
  actionCodes: readonly string[];
  actionLabel: (action: string) => string;
  t: (key: string) => string;
}) {
  const {
    actionDraft,
    tenantDraft,
    onActionChange,
    onTenantChange,
    onSubmit,
    onClear,
    filtersActive,
    actionCodes,
    actionLabel,
    t,
  } = props;

  return (
    <form
      onSubmit={onSubmit}
      className="row g-2 align-items-end mb-3 pb-3 border-bottom"
    >
      <Col md={4} sm={6}>
        <Label for="sa-al-action" className="form-label small mb-1">
          {t("sa.activityLogs.filter.action")}
        </Label>
        <Input
          id="sa-al-action"
          type="select"
          bsSize="sm"
          value={actionDraft}
          onChange={(e) => onActionChange(e.target.value)}
        >
          <option value="">{t("sa.activityLogs.filter.actionAll")}</option>
          {actionCodes.map((code) => (
            <option key={code} value={code}>
              {actionLabel(code)}
            </option>
          ))}
        </Input>
      </Col>
      <Col md={3} sm={6}>
        <Label for="sa-al-tenant" className="form-label small mb-1">
          {t("sa.activityLogs.filter.tenantId")}
        </Label>
        <Input
          id="sa-al-tenant"
          type="number"
          bsSize="sm"
          min={1}
          inputMode="numeric"
          placeholder={t("sa.activityLogs.filter.tenantPlaceholder")}
          value={tenantDraft}
          onChange={(e) => onTenantChange(e.target.value)}
        />
      </Col>
      <Col md={5} className="d-flex flex-wrap gap-2">
        <Button color="primary" size="sm" type="submit">
          {t("sa.activityLogs.filter.apply")}
        </Button>
        <Button
          color="light"
          size="sm"
          type="button"
          disabled={!filtersActive && !actionDraft && !tenantDraft}
          onClick={onClear}
        >
          {t("sa.activityLogs.filter.clear")}
        </Button>
      </Col>
    </form>
  );
}

export default SuperAdminActivityLogs;
